import { useCareproviderAccountContext } from "context/CareproviderAccountContext";
import {
  DOCUMENT_DATA_PROCESSING_AGREEMENT,
  DOCUMENT_TERMS_AND_CONDITIONS,
  RECARE_INFO_EMAIL,
  TRACK_EVENTS,
} from "core/consts";
import { hasAccessToAdmissions } from "core/model/accounts";
import { hasAccessToReverseSearch } from "core/model/careproviders";
import { Careprovider } from "core/types";
import { FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { DropdownNavbarMenuItem } from "ds/ui";
import { showExtensionsTab } from "dsl/ecosystems/CareproviderSettings/shared";
import { useCareproviderHandlerUrls } from "dsl/hooks/useNavigationHandlers";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import {
  BedIcon,
  BlocksIcon,
  BookOpenTextIcon,
  BuildingIcon,
  ClipboardListIcon,
  FileCogIcon,
  FlagIcon,
  KeyRoundIcon,
  LinkIcon,
  LogOutIcon,
  MailIcon,
  SearchIcon,
  SmartphoneIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { useAllowPWA } from "../BrowserNotificationContext/NotificationPermission";
import { useLegalDocuments } from "../LegalDocuments";

type MenuItemsProps = {
  isMobileNavigation?: boolean;
};

type TopMenuItemsProps = MenuItemsProps & {
  careprovider: Careprovider;
};

function useGetTopMenuItems({
  careprovider,
}: TopMenuItemsProps): DropdownNavbarMenuItem[] {
  const { loggedAccount } = useCareproviderAccountContext();
  const careproviderUrls = useCareproviderHandlerUrls();
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const allowPWA = useAllowPWA();
  const { getDocument } = useLegalDocuments();
  const showNewChip =
    getDocument(DOCUMENT_DATA_PROCESSING_AGREEMENT)?.needs_to_sign ||
    getDocument(DOCUMENT_TERMS_AND_CONDITIONS)?.needs_to_sign;
  const showExtensionTab = showExtensionsTab({ careprovider });
  const hasAdmissionsRole = hasAccessToAdmissions(loggedAccount, careprovider);

  const items: (DropdownNavbarMenuItem | false)[] = [
    {
      href: careproviderUrls.goToCareproviderSettingsAccount(),
      hrefMatch: "/careprovider/dashboard/:id/settings/account/*",
      icon: (
        <UserIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "account",
      label: translations.careproviderApp.settings.sidebar.personalDetails,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_ACCOUNT });
      },
    },
    {
      href: careproviderUrls.goToCareproviderSettingsCompanyProfile(),
      hrefMatch: "/careprovider/dashboard/:id/settings/profile/*",
      icon: (
        <BuildingIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "profile",
      label: translations.careproviderApp.settings.sidebar.companyProfile,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_COMPANY });
      },
    },
    {
      href: careproviderUrls.goToCareproviderSettingsServices(),
      hrefMatch: "/careprovider/dashboard/:id/settings/services/*",
      icon: (
        <ClipboardListIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "services",
      label: translations.careproviderApp.settings.sidebar.ourServices,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_SERVICES });
      },
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.receiver_settings.steps.services.key,
        tourKey: PRODUCT_TOURS.receiver_settings.key,
      }),
    },
    {
      href: careproviderUrls.goToCareproviderSettingsTeam(),
      hrefMatch: "/careprovider/dashboard/:id/settings/team/*",
      icon: (
        <UsersIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "team",
      label: translations.careproviderApp.settings.sidebar.teamManagement,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_TEAM });
      },
    },
    {
      href: careproviderUrls.goToCareproviderSettingsPartnerClinics(),
      hrefMatch: "/careprovider/dashboard/:id/settings/partner-clinics/*",
      icon: (
        <LinkIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "partner_clinics",
      label: translations.careproviderApp.settings.sidebar.partnerClinics,
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_PARTNER_CLINICS,
        });
      },
    },
    {
      href: careproviderUrls.goToCareproviderSettingsLegalDocuments(),
      hrefMatch: "/careprovider/dashboard/:id/settings/legal-documents/*",
      icon: (
        <BookOpenTextIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "legal-documents",
      label: translations.careproviderApp.settings.sidebar.legal,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_LEGAL });
      },
      showNewChip: showNewChip,
    },
    {
      href: careproviderUrls.goToCareproviderSettingsCapacity(),
      hrefMatch: "/careprovider/dashboard/:id/settings/capacity/*",
      icon: (
        <BedIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "capacity",
      label: translations.careproviderApp.settings.sidebar.ourCapacity,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_CAPACITY });
      },
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.receiver_settings.steps.capacity.key,
        tourKey: PRODUCT_TOURS.receiver_settings.key,
      }),
    },
    hasAccessToReverseSearch(careprovider) && {
      href: careproviderUrls.goToCareproviderSettingsSearch(),
      hrefMatch: "/careprovider/dashboard/:id/settings/search/*",
      icon: (
        <SearchIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "search",
      label: translations.careproviderApp.settings.sidebar.findPatients,
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_SEARCH,
        });
      },
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.receiver_settings.steps.reverse_search.key,
        tourKey: PRODUCT_TOURS.receiver_settings.key,
      }),
    },
    {
      href: careproviderUrls.goToCareproviderSettingsNotifications(),
      hrefMatch: "/careprovider/dashboard/:id/settings/notifications/*",
      icon: (
        <MailIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "notifications",
      label: translations.careproviderApp.settings.sidebar.notifications,
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_NOTIFICATIONS,
        });
      },
    },
    hasAdmissionsRole && {
      href: careproviderUrls.goToCareproviderSettingsAdmissions(),
      hrefMatch: "/careprovider/dashboard/:id/settings/admissions/*",
      icon: (
        <FileCogIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "admissions",
      label: translations.careproviderApp.settings.sidebar.settingsAdmissions,
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_ADMISSIONS,
        });
      },
      showNewChip: true,
    },
    !!showExtensionTab && {
      href: careproviderUrls.goToCareproviderSettingsExtensions(),
      hrefMatch: "/careprovider/dashboard/:id/settings/extensions/*",
      icon: (
        <BlocksIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "extensions",
      label: translations.careproviderApp.settings.extensions.title,
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_EXTENSIONS,
        });
      },
    },
    allowPWA && {
      href: careproviderUrls.goToCareproviderSettingsPWA(),
      hrefMatch: "/careprovider/dashboard/:id/settings/pwa/*",
      icon: (
        <SmartphoneIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "pwa",
      label: translations.pwaSettings.settingsTitle,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_PWA });
      },
    },
  ];

  return items.truthy();
}

function useGetBottomMenuItems(): DropdownNavbarMenuItem[] {
  const careproviderUrls = useCareproviderHandlerUrls();
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  const items: (DropdownNavbarMenuItem | false)[] = [
    {
      href: `mailto:${RECARE_INFO_EMAIL}?subject=${encodeURIComponent(
        translations.general.reportContent,
      )}`,
      icon: (
        <FlagIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "report_content",
      label: translations.providersearch.footer.reportContent,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.REPORT_CONTENT_CLICKED });
      },
    },
    {
      href: careproviderUrls.goToChangePassword(),
      icon: (
        <KeyRoundIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "change_password",
      label: translations.careproviderAccount.changePassword,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CHANGE_PASSWORD_CLICKED });
      },
    },
    {
      href: careproviderUrls.replaceByAuth(),
      icon: (
        <LogOutIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "logout",
      label: translations.general.logOut,
    },
  ];

  return items.truthy();
}

export function useReceiverMenuItems({
  careprovider,
  isMobileNavigation,
}: {
  careprovider: Careprovider;
  isMobileNavigation?: boolean;
}) {
  const menuItemsTop = useGetTopMenuItems({
    careprovider,
    isMobileNavigation,
  });
  const menuItemsBottom = useGetBottomMenuItems();

  return { menuItemsBottom, menuItemsTop };
}
